import * as React from 'react';

import { NewBadge } from '../../components/common/NewBadge';
import { UnreadBadge } from '../../components/common/UnreadBadge';

/** Which deliverable this action governs. */
export type ReviewActionPhase = 'script' | 'content';

const REVIEW_STATUS: Record<ReviewActionPhase, string> = {
  script: 'script_review',
  content: 'content_review',
};

export interface ReviewActionProps {
  /** The row's production status (e.g. 'content_pending' / 'script_review'). */
  status: string;
  phase: ReviewActionPhase;
  /** Current version of this phase's asset (1 = first upload, increments per
   *  revision). Drives the "· vN" tag and (together with hasEta/seen) the
   *  floating "New" badge. */
  version?: number;
  unreadCount?: number;
  /** Whether an ETA has been set for the NEXT revision (see EtaTimeline,
   *  which renders it in the Updated column). Setting one is itself a signal
   *  the admin has acknowledged this revision, so it clears "New" — same as
   *  the caller opening the review drawer (see `seen`). */
  hasEta?: boolean;
  /** Whether the CALLING viewer has already opened this version (per-user,
   *  BE-tracked — see useMarkPostSeen). Clears "New" independently of hasEta:
   *  either opening it or an ETA landing is enough. */
  seen?: boolean;
  /** Toggle the review dialog for this row. */
  onToggleReview: () => void;
}

/**
 * Action cell for the Scripts/Content tabs: the Review link once the
 * deliverable has arrived (*_review), with a "· vN" tag once a revision has
 * landed and a floating "New" badge while that revision is both unseen by
 * this viewer AND has no forward ETA set yet (either one clears it — see
 * EtaTimeline, which owns the actual ETA; this component is Review-only).
 * Renders nothing for any other status — no dash, no placeholder; the
 * reupload button next to this cell is the row's only other action while
 * pending.
 */
export function ReviewAction({
  status,
  phase,
  version,
  unreadCount,
  hasEta,
  seen,
  onToggleReview,
}: ReviewActionProps): React.ReactElement | null {
  if (status !== REVIEW_STATUS[phase]) return null;

  const hasVersion = !!version && version > 1;
  // A revision landed (version > 1) but this viewer hasn't opened it AND no
  // forward-looking ETA has been set for the next one yet — nudge whoever's
  // looking that something's fresh. Clears the moment either happens.
  const isFreshRevision = hasVersion && !hasEta && !seen;
  const hasUnread = (unreadCount ?? 0) > 0;

  return (
    <div className="flex items-center gap-1.5">
      <div className="relative inline-flex">
        <button
          type="button"
          onClick={onToggleReview}
          className="text-body-sm-medium text-(--brand-500) transition-colors hover:underline"
        >
          Review
        </button>
        {/* Mutually exclusive, both top-right: the unread count wins the corner
         *  the moment a message is unread; the "New" pill only shows while there
         *  are no unread messages (a fresh, unseen revision). */}
        {hasUnread ? (
          <UnreadBadge count={unreadCount} variant="floating" />
        ) : (
          <NewBadge show={isFreshRevision} />
        )}
      </div>
      {hasVersion && (
        <span className="text-caption-medium text-muted-foreground tabular-nums">· v{version}</span>
      )}
    </div>
  );
}
